// MINDESTBESTELLWERT
// v2 (14.09.2026): Observer laedt /cart.js nicht nach, wenn der Zustand juenger als FRISCH_MS ist (Theme-Event
// hat ihn gerade geliefert); dann wird nur applyState() wiederholt. Spart einen Request je Warenkorb-Aktion.
(() => {
  // Mindestbestellwert in Cent (1500 = 15,00 €) — einzige Stelle, Text wird abgeleitet
  const MIN_CENTS = 1500;

  const style = document.createElement("style");
  style.textContent = `
    .min-order-disabled {
      opacity: .5 !important;
      cursor: not-allowed !important;
      pointer-events: none !important;
    }
    .min-order-hidden {
      display: none !important;
    }
    .min-order-note {
      font-size: 1.1rem;
      margin-top: 0.75rem;
      opacity: 1 !important;
      text-align: center;
      color: inherit;
    }
    .min-order-note .min-order-amount,
    .min-order-note .min-order-label {
      font-weight: 700;
    }
  `;
  document.head.appendChild(style);

  const formatEUR = (cents) => (cents / 100).toFixed(2).replace(".", ",") + " €";
  const DISABLED_TEXT = "Mindestbestellwert " + formatEUR(MIN_CENTS);

  const getCheckoutButtons = () =>
    Array.from(document.querySelectorAll('button[name="checkout"], input[name="checkout"]'));

  // Express-Wallets (Shop Pay, PayPal …) laufen am checkout-Button vorbei —
  // unter Mindestbestellwert werden sie ausgeblendet
  const getWalletContainers = () =>
    Array.from(document.querySelectorAll(
      "shopify-accelerated-checkout-cart, .dynamic-checkout__content, .additional-checkout-buttons"
    ));

  // null = Zustand unbekannt (noch nicht geladen oder fetch fehlgeschlagen)
  let lastTotal = null;
  let lastItemCount = null;
  // zählt akzeptierte Zustands-Schreibungen — ein Fetch-Ergebnis wird verworfen,
  // wenn währenddessen ein neuerer Zustand (Theme-Event) geschrieben wurde
  let writeSeq = 0;
  // v2: Zeitpunkt des letzten akzeptierten Zustands; binnen FRISCH_MS gilt er als aktuell
  const FRISCH_MS = 2000;
  let lastStateAt = 0;

  const disableBtn = (btn) => {
    if (!btn.dataset.minOrderOriginal) {
      btn.dataset.minOrderOriginal = btn.innerHTML.trim();
    }
    // idempotent schreiben — sonst füttert jede Anwendung den MutationObserver
    if (!btn.classList.contains("min-order-disabled")) btn.classList.add("min-order-disabled");
    if (btn.getAttribute("aria-disabled") !== "true") btn.setAttribute("aria-disabled", "true");
    if (!btn.hasAttribute("disabled")) btn.setAttribute("disabled", "disabled");
    if (btn.textContent.trim() !== DISABLED_TEXT) btn.textContent = DISABLED_TEXT;
  };

  const enableBtn = (btn) => {
    if (btn.classList.contains("min-order-disabled")) btn.classList.remove("min-order-disabled");
    if (btn.hasAttribute("aria-disabled")) btn.removeAttribute("aria-disabled");
    if (btn.hasAttribute("disabled")) btn.removeAttribute("disabled");
    if (btn.dataset.minOrderOriginal && btn.innerHTML.trim() !== btn.dataset.minOrderOriginal) {
      btn.innerHTML = btn.dataset.minOrderOriginal;
    }
  };

  // Hinweis je Checkout-Button — funktioniert damit im Drawer UND auf /cart,
  // wo keiner der Drawer-Summary-Container existiert
  const renderNotes = (isBelowMin, totalCents) => {
    getCheckoutButtons().forEach((btn) => {
      const parent = btn.parentElement;
      if (!parent) return;
      let note = parent.querySelector(":scope > .min-order-note");
      if (isBelowMin) {
        if (!note) {
          note = document.createElement("p");
          note.className = "min-order-note";
          note.setAttribute("role", "status");
          btn.insertAdjacentElement("afterend", note);
        }
        const diff = MIN_CENTS - totalCents;
        const html =
          `Noch <span class="min-order-amount">${formatEUR(diff)}</span> ` +
          `bis zum <span class="min-order-label">Mindestbestellwert</span> von ${formatEUR(MIN_CENTS)}.`;
        if (note.innerHTML !== html) note.innerHTML = html;
      } else if (note) {
        note.remove();
      }
    });
  };

  const applyState = () => {
    if (lastTotal === null || lastItemCount === null) return;
    const isBelowMin = lastItemCount > 0 && lastTotal < MIN_CENTS;
    getCheckoutButtons().forEach((btn) => (isBelowMin ? disableBtn(btn) : enableBtn(btn)));
    getWalletContainers().forEach((el) => {
      if (isBelowMin !== el.classList.contains("min-order-hidden")) {
        el.classList.toggle("min-order-hidden", isBelowMin);
      }
    });
    renderNotes(isBelowMin, lastTotal);
  };

  const setState = (total, itemCount) => {
    writeSeq++;
    lastTotal = total;
    lastItemCount = itemCount;
    lastStateAt = Date.now();
    applyState();
  };

  let evaluating = false;
  let evalQueued = false;

  const evaluate = async (cartData) => {
    // Theme-Event liefert den frischen Cart mit — nur akzeptieren, wenn BEIDE
    // Felder da sind, sonst regulär nachladen
    if (cartData &&
        typeof cartData.total_price === "number" &&
        typeof cartData.item_count === "number") {
      setState(cartData.total_price, cartData.item_count);
      return;
    }
    if (evaluating) {
      // nicht verwerfen — nach dem laufenden Fetch nachholen
      evalQueued = true;
      return;
    }
    evaluating = true;
    const seqAtStart = writeSeq;
    try {
      const res = await fetch("/cart.js", { credentials: "same-origin" });
      if (!res.ok) throw new Error("cart.js " + res.status);
      const cart = await res.json();
      // veraltetes Ergebnis: währenddessen kam ein neuerer Zustand herein
      if (writeSeq === seqAtStart) {
        setState(cart.total_price ?? 0, cart.item_count ?? 0);
      }
    } catch (e) {
      // Fehler => letzten bekannten Zustand behalten, NICHT auf 0 fallen
      // (0 hätte den Checkout bei jedem Netzwerkfehler gesperrt)
      console.warn("[min-order] /cart.js nicht ladbar:", e);
    } finally {
      evaluating = false;
      if (evalQueued) {
        evalQueued = false;
        evaluate();
      }
    }
  };

  const wireListeners = () => {
    // Events dieses Themes (Clean Canvas) — detail.cart spart den Roundtrip
    ["on:cart:add", "on:line-item:change"].forEach((name) => {
      document.addEventListener(name, (ev) => evaluate(ev.detail && ev.detail.cart));
    });
    // Legacy-Namen, falls eine App sie feuert
    document.addEventListener("cart:updated", () => evaluate());
    document.addEventListener("cart:refresh", () => evaluate());

    // bfcache-Rückkehr zeigt sonst veralteten Zustand
    window.addEventListener("pageshow", (ev) => { if (ev.persisted) evaluate(); });

    // Sicherheitsnetz: Re-Render von Drawer/Cart-Bereichen erneut bewerten —
    // auf body beobachtet (überlebt Node-Austausch), aber auf Cart-UI gefiltert
    let debounceTO = null;
    const mo = new MutationObserver((mutations) => {
      const relevant = mutations.some((m) => {
        const t = m.target;
        return t && t.closest && t.closest(
          'form[action="/cart"], [id$="__cart-drawer"], #cart-summary-and-blocks, cart-drawer, #cart-drawer'
        );
      });
      if (!relevant) return;
      clearTimeout(debounceTO);
      // v2: frischer Zustand -> nur erneut anwenden (Re-Render hat Klassen/Hinweis verworfen), kein Fetch
      debounceTO = setTimeout(() => (Date.now() - lastStateAt < FRISCH_MS ? applyState() : evaluate()), 150);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    // Delegiert statt je Formular gebunden — überlebt Form-Re-Render.
    // Zustand unbekannt (lastTotal === null) => nicht blocken
    document.addEventListener("submit", (ev) => {
      const form = ev.target;
      if (!form.matches || !form.matches('form[action="/cart"], form[action^="/cart?"]')) return;
      const submitter = ev.submitter;
      if (submitter && submitter.name && submitter.name !== "checkout") return;
      if (typeof lastTotal === "number" && lastItemCount > 0 && lastTotal < MIN_CENTS) {
        ev.preventDefault();
        ev.stopPropagation();
        evaluate();
      }
    }, true);
  };

  const onReady = () => {
    wireListeners();
    evaluate();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }
})();